import { db } from './firebase';
import {
  doc,
  setDoc,
  deleteDoc,
  getDoc,
  getDocFromCache,
  onSnapshot,
  updateDoc,
  increment,
  DocumentData,
  Unsubscribe,
} from 'firebase/firestore';
import { collection, getDocs } from 'firebase/firestore';

// Paths
const userDoc = (userDocId: string) => doc(db, 'users', userDocId);
const followingDoc = (meDocId: string, targetDocId: string) => doc(db, 'users', meDocId, 'following', targetDocId);
const followersDoc = (targetDocId: string, followerUid: string) => doc(db, 'users', targetDocId, 'followers', followerUid);

export type ToggleFollowResult = { following: boolean };

export const FollowService = {
  // Resolve the Firestore user document ID for a given auth UID.
  // Many apps key user docs by UID, but some use separate IDs.
  _meIdCache: new Map<string, string>(),
  async resolveUserDocIdForUid(authUid: string): Promise<string> {
    if (!authUid) throw new Error('No auth UID');
    const cached = this._meIdCache.get(authUid);
    if (cached) return cached;

    // Fast path: assume users/{uid} exists
    try {
      const direct = await getDoc(userDoc(authUid));
      if (direct.exists()) {
        this._meIdCache.set(authUid, authUid);
        return authUid;
      }
    } catch {}

    // Fallback: look up a user doc where a stored uid field matches
    // Note: import kept minimal; using getDoc only. If you prefer query, consider adding it.
    // To avoid extra indexes, we keep this simple and expect direct path to usually exist.
    // If no direct match, we cannot proceed safely.
    throw new Error('Could not resolve user document for current user');
  },
  async toggleFollow(currentUserUid: string, targetUserDocId: string): Promise<ToggleFollowResult> {
    if (!currentUserUid) throw new Error('Not authenticated');
    if (!targetUserDocId) throw new Error('Missing target user');

    // Resolve my user doc id (may equal auth uid)
    const meDocId = await this.resolveUserDocIdForUid(currentUserUid);
    const targetDocId = targetUserDocId;

    if (meDocId === targetDocId) throw new Error('Cannot follow yourself');

    const fRef = followingDoc(meDocId, targetDocId);
    const rRef = followersDoc(targetDocId, currentUserUid);
    const meRef = userDoc(meDocId);
    const targetRef = userDoc(targetDocId);

    // Check if already following
    const existing = await getDoc(fRef);
    const isFollowing = existing.exists();

    if (!isFollowing) {
      // Follow (create relationship docs and bump counters)
      try {
        await setDoc(fRef, { createdAt: new Date() });
        console.log('followService: set following OK');
      } catch (e) {
        console.error('followService: set following DENIED/FAILED', e);
        throw e;
      }
      try {
        await setDoc(rRef, { createdAt: new Date(), uid: currentUserUid });
        console.log('followService: set follower OK');
      } catch (e) {
        console.error('followService: set follower DENIED/FAILED', e);
        throw e;
      }
      // Counter updates (allowed via counter-only rule)
      try {
        await updateDoc(meRef, { following: increment(1) });
        console.log('followService: inc me.following OK');
      } catch (e) {
        console.error('followService: inc me.following DENIED/FAILED', e);
        throw e;
      }
      try {
        await updateDoc(targetRef, { followers: increment(1) });
        console.log('followService: inc target.followers OK');
      } catch (e) {
        console.error('followService: inc target.followers DENIED/FAILED', e);
        throw e;
      }
      console.log(`✅ Followed: users/${targetDocId}`);
      return { following: true };
    } else {
      // Unfollow (delete relationship docs and decrement counters)
      try {
        await deleteDoc(fRef);
        console.log('followService: delete following OK');
      } catch (e) {
        console.error('followService: delete following DENIED/FAILED', e);
        throw e;
      }
      try {
        await deleteDoc(rRef);
        console.log('followService: delete follower OK');
      } catch (e) {
        console.error('followService: delete follower DENIED/FAILED', e);
        throw e;
      }
      try {
        await updateDoc(meRef, { following: increment(-1) });
        console.log('followService: dec me.following OK');
      } catch (e) {
        console.error('followService: dec me.following DENIED/FAILED', e);
        throw e;
      }
      try {
        await updateDoc(targetRef, { followers: increment(-1) });
        console.log('followService: dec target.followers OK');
      } catch (e) {
        console.error('followService: dec target.followers DENIED/FAILED', e);
        throw e;
      }
      console.log(`✅ Unfollowed: users/${targetDocId}`);
      return { following: false };
    }
  },

  // Check if current user follows target profile (one-time, no subscription)
  async checkIsFollowing(currentUserUid: string, targetUserDocId: string): Promise<boolean> {
    try {
      const meDocId = await this.resolveUserDocIdForUid(currentUserUid);
      const fRef = followingDoc(meDocId, targetUserDocId);
      const snap = await getDoc(fRef);
      return snap.exists();
    } catch {
      return false;
    }
  },

  // Subscribe to whether current user follows target profile
  subscribeIsFollowing(currentUserUid: string, targetUserDocId: string, cb: (following: boolean) => void): Unsubscribe {
    let innerUnsub: Unsubscribe | undefined;
    let cancelled = false;
    let last: boolean | undefined;

    // Attempt fast path assuming users/{uid} exists while we resolve definitively
    const tryAttach = (meDocId: string) => {
      const fRef = followingDoc(meDocId, targetUserDocId);
      // Prime from cache
      getDocFromCache(fRef)
        .then((snap) => {
          const v = snap.exists();
          if (v !== last) {
            last = v;
            cb(v);
          }
        })
        .catch(() => {});
      innerUnsub = onSnapshot(fRef, { includeMetadataChanges: true }, (snap) => {
        const v = snap.exists();
        if (v !== last) {
          last = v;
          cb(v);
        }
      });
    };

    // Attach immediately with assumed id
    tryAttach(currentUserUid);

    // Resolve and, if different, reattach
    this.resolveUserDocIdForUid(currentUserUid)
      .then((meDocId) => {
        if (cancelled) return;
        if (meDocId !== currentUserUid) {
          if (innerUnsub) try { innerUnsub(); } catch {}
          tryAttach(meDocId);
        }
      })
      .catch(() => { /* keep assumed subscription */ });

    return () => {
      cancelled = true;
      if (innerUnsub) innerUnsub();
    };
  },

  // Subscribe to counters for a profile
  subscribeUserCounters(userDocId: string, cb: (counters: { followers: number; following: number }) => void): Unsubscribe {
    const uRef = userDoc(userDocId);
    let last: { followers: number; following: number } | undefined;
    // Emit cached counters immediately if available
    getDocFromCache(uRef)
      .then((snap) => {
        const d = snap.data() as DocumentData | undefined;
        const v = {
          followers: Math.max(0, Number(d?.followers || 0)),
          following: Math.max(0, Number(d?.following || 0)),
        };
        if (!last || v.followers !== last.followers || v.following !== last.following) {
          last = v;
          cb(v);
        }
      })
      .catch(() => {/* cache miss is fine */});
    return onSnapshot(uRef, { includeMetadataChanges: true }, (snap) => {
      const d = snap.data() as DocumentData | undefined;
      const v = {
        followers: Math.max(0, Number(d?.followers || 0)),
        following: Math.max(0, Number(d?.following || 0)),
      };
      if (!last || v.followers !== last.followers || v.following !== last.following) {
        last = v;
        cb(v);
      }
    });
  },

  /**
   * Recompute counters from relationship subcollections and write them back to the user doc.
   * Useful to fix drift if counters ever became inconsistent with actual follow relations.
   */
  async reconcileUserCounters(userDocId: string): Promise<{ followers: number; following: number }> {
    const followersCol = collection(db, 'users', userDocId, 'followers');
    const followingCol = collection(db, 'users', userDocId, 'following');
    let followers = 0;
    let following = 0;
    try {
      const [fsnap, gsnap] = await Promise.all([
        getDocs(followersCol),
        getDocs(followingCol),
      ]);
      followers = fsnap.size;
      following = gsnap.size;
    } catch (e) {
      // Best-effort; rethrow so caller can decide
      throw e;
    }
    try {
      await updateDoc(userDoc(userDocId), {
        followers,
        following,
      });
    } catch (e) {
      // Surface write error to caller
      throw e;
    }
    return { followers, following };
  },
};
